/**
 * SearchInput Component
 *
 * @module shared/components/data-display/SearchInput
 *
 * Reusable search input with debounce, search icon, and clear button.
 * Provides a consistent search experience across the application.
 *
 * @example
 * ```tsx
 * <SearchInput
 *   value={searchText}
 *   onChange={handleSearch}
 *   placeholder="Search cases..."
 *   debounceMs={500}
 * />
 * ```
 */

import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import { useDebounce } from '@/shared/hooks';

// ============================================================================
// Types
// ============================================================================

export interface SearchInputProps {
  /**
   * Current search value
   */
  value: string;
  /**
   * Debounced change handler (called after debounce delay)
   */
  onChange: (value: string) => void;
  /**
   * Optional immediate clear handler
   */
  onClear?: () => void;
  /**
   * Input placeholder text
   */
  placeholder?: string;
  /**
   * Debounce delay in milliseconds
   * @default 500
   */
  debounceMs?: number;
  /**
   * Input size
   * @default 'small'
   */
  size?: 'small' | 'medium';
  /**
   * Whether input should take full width
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
  /**
   * MUI sx prop for styling
   */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Component
// ============================================================================

export function SearchInput({
  value,
  onChange,
  onClear,
  placeholder = 'Search...',
  debounceMs = 500,
  size = 'small',
  fullWidth = false,
  className,
  disabled = false,
  sx,
}: SearchInputProps) {
  const [localValue, setLocalValue] = useState(value);
  const debouncedValue = useDebounce(localValue, debounceMs);

  // Update parent when debounced value changes and has settled to the current input
  useEffect(() => {
    if (debouncedValue !== value && debouncedValue === localValue) {
      onChange(debouncedValue);
    }
  }, [debouncedValue, localValue, onChange, value]);

  // Sync with external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
    onClear?.();
  };

  return (
    <TextField
      size={size}
      fullWidth={fullWidth}
      placeholder={placeholder}
      value={localValue}
      onChange={(e) => handleChange(e.target.value)}
      disabled={disabled}
      {...(className !== undefined && { className })}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
        endAdornment: localValue && (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleClear} edge="end" aria-label="Clear search">
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={[
        {
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
          },
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    />
  );
}
